import React, { useState, useCallback, memo } from 'react';
import { Skeleton } from './ui/skeleton';
import { Image as ImageIcon, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SmartImage - A robust, memoized image component with lazy-loading,
 * skeleton placeholder, and graceful error fallback.
 * 
 * Features:
 * - Accepts both uploaded URLs (relative) and external URLs (absolute)
 * - Native lazy loading + async decoding for performance
 * - Fixed height to prevent layout shifts
 * - Skeleton placeholder while loading
 * - Clean fallback UI on error (no broken image icon)
 * - Admin mode shows "Re-upload required" message for missing images
 * - Never renders empty <img> - always shows skeleton or fallback
 * - Memoized to prevent unnecessary re-renders
 */
const SmartImage = memo(({ 
  src, 
  alt = '', 
  height = 'h-48',  // Default 192px, matches Saturday Program card
  className = '',
  fallbackText = 'Image unavailable',
  showFallbackIcon = true,
  objectFit = 'object-cover',
  hoverScale = true,
  isAdmin = false,  // When true, show "Re-upload required" message
  isMissing = false,  // Explicitly marked as missing (from backend migration)
  onReuploadClick = null,  // Callback when admin clicks re-upload
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Construct full URL for relative paths (uploaded images)
  const getFullUrl = useCallback((url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    // Relative path - prepend backend URL
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  }, []);

  const handleLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setError(true);
    setLoaded(true); // Stop showing skeleton on error
  }, []);

  const fullUrl = getFullUrl(src);
  
  // Special fallback for admin mode with missing images
  const adminMissingContent = (
    <div 
      className="w-full h-full bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50 flex items-center justify-center cursor-pointer hover:from-amber-100 hover:to-amber-100 transition-colors"
      onClick={onReuploadClick}
    >
      <div className="text-center text-amber-700 px-4">
        <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2" />
        <span className="text-xs sm:text-sm font-medium block">Image missing</span>
        <span className="text-xs text-amber-600">Click to re-upload</span>
      </div>
    </div>
  );
  
  // Standard fallback content for missing or errored images
  const fallbackContent = (
    <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
      <div className="text-center text-slate-400 px-4">
        {showFallbackIcon && (
          <ImageIcon className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 opacity-50" />
        )}
        <span className="text-xs sm:text-sm">{fallbackText}</span>
      </div>
    </div>
  );

  // If explicitly marked as missing and is admin, show re-upload prompt
  if (isMissing && isAdmin) {
    return (
      <div 
        className={`relative ${height} overflow-hidden ${className}`}
        data-testid="smart-image-missing-admin"
      >
        {adminMissingContent}
      </div>
    );
  }

  // No source or error - show fallback (admin gets re-upload prompt if error)
  if (!fullUrl || error) {
    // If admin and image failed to load, show re-upload option
    if (isAdmin && (error || isMissing)) {
      return (
        <div 
          className={`relative ${height} overflow-hidden ${className}`}
          data-testid="smart-image-error-admin"
        >
          {adminMissingContent}
        </div>
      );
    }
    
    return (
      <div 
        className={`relative ${height} overflow-hidden ${className}`}
        data-testid="smart-image-fallback"
      >
        {fallbackContent}
      </div>
    );
  }

  return (
    <div 
      className={`relative ${height} overflow-hidden ${className}`}
      data-testid="smart-image-container"
    >
      {/* Skeleton placeholder - shown until image loads */}
      {!loaded && (
        <div className="absolute inset-0 z-10" data-testid="smart-image-skeleton">
          <Skeleton className="w-full h-full rounded-none" />
        </div>
      )}
      
      {/* Actual image */}
      <img
        src={fullUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`
          w-full h-full ${objectFit}
          transition-all duration-300
          ${loaded ? 'opacity-100' : 'opacity-0'}
          ${hoverScale ? 'group-hover:scale-105' : ''}
        `}
        data-testid="smart-image"
      />
    </div>
  );
});

SmartImage.displayName = 'SmartImage';

export { SmartImage };
export default SmartImage;
